import type React from 'react';

import type { BaseItineraryBlockHeaderAttributes } from './BaseItineraryBlock.js';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

/**
 * Layout of a full-page (`lg`) itinerary event: a type eyebrow and display title, optional header
 * attributes and summary, then detail content (callers append their own actions row) — optionally beside a sticky side
 * panel (a map or photo gallery). Layout only: no state, no actions of its own, so the public shared
 * itinerary can render events exactly as the owner sees them, minus the actions.
 */
export function ItineraryLargeBlockLayout({
  icon,
  typeLabel,
  preHeaderContent,
  title,
  headerAttributes,
  summary,
  children,
  panelContent,
  panelPosition = 'left',
  className,
}: {
  icon: React.ReactNode;
  typeLabel: string;
  preHeaderContent?: React.ReactNode;
  title: string;
  headerAttributes?: BaseItineraryBlockHeaderAttributes[];
  summary?: React.ReactNode;
  children?: React.ReactNode;
  panelContent?: React.ReactNode;
  panelPosition?: 'left' | 'right';
  className?: string;
}) {
  // `flex-1` only from `md`: in the stacked mobile column a 0 flex-basis leaves the panel's height
  // indefinite, so the gallery/map's `h-full` resolves to 0 and the panel renders empty. Without it
  // the fixed `h-[60dvh]` is a definite height the panel content can fill.
  const panelClasses = cn(
    'sticky top-0 md:flex-1 flex h-[60dvh] md:h-auto min-h-[60dvh] max-h-[100dvh] bg-background-secondary order-2 md:order-none',
  );

  return (
    <div
      data-print="block"
      className={cn(
        'relative border-b w-full',
        panelContent ? 'flex flex-col md:flex-row justify-center' : 'py-8 md:py-24',
        className,
      )}
    >
      {panelContent && panelPosition === 'left' && <div data-print="block-panel" className={panelClasses}>
          {panelContent}
        </div>}

      <div
        data-print="block-content"
        className={cn(
          'flex flex-col justify-center',
          panelContent ? 'flex-1 py-8 md:py-24' : 'max-w-4xl mx-auto',
          panelContent && panelPosition === 'left' ? 'items-start' : 'items-end',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-6 md:gap-8 w-full px-4 md:px-8',
            panelContent ? 'max-w-[700px]' : 'max-w-prose mx-auto md:items-center',
            panelContent && panelPosition === 'left' && 'md:pl-12',
          )}
        >
          <div className={cn('flex flex-col gap-1.5', !panelContent && 'md:items-center')}>
            <div className="flex items-center gap-2 mb-1">
              {icon}
              <div className="text-xs font-medium uppercase tracking-wide">{typeLabel}</div>

              {preHeaderContent}
            </div>

            <h3 className={cn('text-2xl md:text-3xl font-normal font-display', !panelContent && 'md:text-center')}>
              {title}
            </h3>

            {headerAttributes && headerAttributes.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                {headerAttributes.map((attribute) => (
                  <div className="flex items-center gap-1" key={attribute.label}>
                    {attribute.icon && <MaterialIcon icon={attribute.icon} size={IconSizes.xs} className="flex-shrink-0" />}
                    <div>{attribute.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {summary}

          {children}
        </div>
      </div>

      {panelContent && panelPosition === 'right' && <div data-print="block-panel" className={panelClasses}>
          {panelContent}
        </div>}
    </div>
  );
}
